import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    InvoiceReference,
    MAX_INVOICE_REFERENCES,
} from "@common/schemas/invoiceReference.schema";
import {
    DraftRefsState,
    DraftRefsTarget,
    useDraftRefsByTarget,
    useTargetDraftRefs,
} from "./useDraftRefsByTarget";

export interface InvoiceRefContextValue extends DraftRefsState<InvoiceReference> {
    focusedInvoiceId: string | null;
    setFocusedInvoiceId: React.Dispatch<React.SetStateAction<string | null>>;
}

function buildDraftRefDedupKey(ref: InvoiceReference): string {
    return `${ref.taskId}::${ref.invoiceId}`;
}

const InvoiceRefContext = createContext<InvoiceRefContextValue | null>(null);

interface InvoiceRefProviderProps {
    taskId: string;
    children: React.ReactNode;
}

export const InvoiceRefProvider: React.FC<InvoiceRefProviderProps> = ({
    taskId,
    children,
}) => {
    const draftRefsState = useDraftRefsByTarget({
        taskId,
        maxRefs: MAX_INVOICE_REFERENCES,
        getDedupKey: buildDraftRefDedupKey,
    });
    const [focusedInvoiceId, setFocusedInvoiceId] = useState<string | null>(
        null,
    );

    useEffect(() => {
        setFocusedInvoiceId(null);
    }, [taskId]);

    const value = useMemo(
        () => ({ ...draftRefsState, focusedInvoiceId, setFocusedInvoiceId }),
        [draftRefsState, focusedInvoiceId],
    );

    return (
        <InvoiceRefContext.Provider value={value}>
            {children}
        </InvoiceRefContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export function useInvoiceRefContextMaybe(): InvoiceRefContextValue | null {
    return useContext(InvoiceRefContext);
}

// eslint-disable-next-line react-refresh/only-export-components
export function useDraftInvoiceRefsForTarget(target: DraftRefsTarget) {
    return useTargetDraftRefs({
        draftRefsState: useInvoiceRefContextMaybe(),
        target,
    });
}
